// Hex grid math + procedural terrain generation.
// Extracted from legacy/tsuri.html — logic and constants are unchanged.

export interface Axial {
  q: number;
  r: number;
}

export interface WorldXZ {
  x: number;
  z: number;
}

export const HEX_R = 0.62;
// Vertical distance between stacked terrain layers. Deliberately a third of
// the hex's own scale so the board reads as shallow tiles rather than a
// voxel column world — terrain heights are still whole levels, they're just
// drawn shorter. Everything vertical derives from this, so offsets tuned
// against it must be expressed as multiples of HEX_H, not baked numbers.
export const HEX_H = 1 / 9;

// Everything that sits *on* the board — buildings, trees, the fisherman,
// his boat, ground cover — is drawn at this fraction of its modelled size,
// so a market occupies roughly one hex instead of spanning three. The hexes
// themselves are unaffected, and so is every distance in game logic (chop
// and cast range, movement speed, load radii), which stays in hex-relative
// world units. This is presentation only.
export const PROP_SCALE = 1 / 3;
const SQRT3 = Math.sqrt(3);

export function hexToWorld(q: number, r: number): WorldXZ {
  return { x: HEX_R * 1.5 * q, z: HEX_R * SQRT3 * (r + q / 2) };
}

export function hexRound(qf: number, rf: number): Axial {
  const x = qf, z = rf, y = -x - z;
  let rx = Math.round(x), ry = Math.round(y), rz = Math.round(z);
  const dx = Math.abs(rx - x), dy = Math.abs(ry - y), dz = Math.abs(rz - z);
  if (dx > dy && dx > dz) rx = -ry - rz;
  else if (dy > dz) ry = -rx - rz;
  else rz = -rx - ry;
  return { q: rx, r: rz };
}

export function worldToHex(x: number, z: number): Axial {
  const qf = (2 / 3 * x) / HEX_R;
  const rf = (-1 / 3 * x + SQRT3 / 3 * z) / HEX_R;
  return hexRound(qf, rf);
}

export function axialDistance(q1: number, r1: number, q2: number, r2: number): number {
  return (Math.abs(q1 - q2) + Math.abs(q1 + r1 - q2 - r2) + Math.abs(r1 - r2)) / 2;
}

export const HEX_SIDE_DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 0], [0, 1], [-1, 1], [-1, 0], [0, -1], [1, -1],
];

export const SEA_LEVEL = 0;
export const GEN_DEPTH = 6;
export const PEAK_LEVEL = 8;

let seedQ = 0, seedR = 0;

export function applySeed(n: number): void {
  seedQ = (n * 2654435761 % 1000003) / 10;
  seedR = (n * 40503 % 999983) / 10;
}

export function getTerrainSeed(): Axial {
  return { q: seedQ, r: seedR };
}

export function hash2(ix: number, iz: number): number {
  let n = ix * 374761393 + iz * 668265263;
  n = (n ^ (n >> 13)) * 1274126177;
  n = n ^ (n >> 16);
  return ((n & 0x7fffffff) / 0x7fffffff);
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function valueNoise2D(x: number, z: number): number {
  const x0 = Math.floor(x), z0 = Math.floor(z);
  const sx = smoothstep(x - x0), sz = smoothstep(z - z0);
  const n00 = hash2(x0, z0), n10 = hash2(x0 + 1, z0);
  const n01 = hash2(x0, z0 + 1), n11 = hash2(x0 + 1, z0 + 1);
  return lerp(lerp(n00, n10, sx), lerp(n01, n11, sx), sz);
}

// Lets world/fixedMap.ts's hand-authored board substitute for the noise
// below, without any of terrain.ts's many callers needing to know about
// it. null (the default) is normal procedural generation; every other
// system that derives from terrainHeightAt (habitats, trees, villages,
// wrecks, spawn point) automatically follows whichever is active.
let heightOverride: ((q: number, r: number) => number) | null = null;

export function setHeightOverride(fn: ((q: number, r: number) => number) | null): void {
  heightOverride = fn;
}

export function terrainHeightAt(q: number, r: number): number {
  if (heightOverride) return heightOverride(q, r);
  let h = 0, amp = 1, freq = 0.045, total = 0;
  for (let o = 0; o < 4; o++) {
    h += valueNoise2D((q + seedQ) * freq, (r + seedR) * freq) * amp;
    total += amp;
    amp *= 0.5; freq *= 2.1;
  }
  return Math.round((h / total - 0.5) * 22);
}

export function surfaceYAt(x: number, z: number): number {
  const hx = worldToHex(x, z);
  const h = terrainHeightAt(hx.q, hx.r);
  return h * HEX_H + HEX_H / 2;
}

export function isWaterAt(x: number, z: number): boolean {
  const hx = worldToHex(x, z);
  return terrainHeightAt(hx.q, hx.r) < SEA_LEVEL;
}
